<template>
  <div class="parent-page">
    <h1>学生列表</h1>
    <ul class="student-list">
      <li v-for="student in students" :key="student.id" class="student-item">
        <span class="student-name">{{ student.studentName }}</span>
        <span class="class-name">{{ student.className }}</span>
        <button 
          class="reserve-btn" 
          :class="{ 'disabled': countdowns[student.id] > 0 }"
          :disabled="countdowns[student.id] > 0"
          @click="handlePickUp(student.id)"
        >
          {{ countdowns[student.id] > 0 ? `${countdowns[student.id]}秒后重试` : '预约接送' }}
        </button>
      </li>
    </ul>
    <div v-if="students.length === 0" class="empty-state">
      <p>暂无学生信息</p>
    </div>
    
    <!-- 自定义弹窗 -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ modalTitle }}</h3>
        </div>
        <div class="modal-body">
          <p>{{ modalMessage }}</p>
        </div>
        <div class="modal-footer">
          <button class="modal-btn" @click="closeModal">确定</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { getParentChildren } from '../api'
import { pickUpChild } from '../api'
import { getToken } from '@/utils/utils'

const students = ref([])
const router = useRouter()
const countdowns = ref({})
const timers = ref({})

// 弹窗状态
const showModal = ref(false)
const modalTitle = ref('')
const modalMessage = ref('')

function showCustomAlert(title, message) {
  modalTitle.value = title
  modalMessage.value = message
  showModal.value = true
}

function closeModal() {
  showModal.value = false
}

async function fetchStudentList() {
  try {
    const res = await getParentChildren()
    if (res.data && res.data.data && res.data.data.dataList) {
      students.value = res.data.data.dataList
    }
  } catch (e) {
    console.error('获取学生列表失败:', e)
  }
}

function startCountdown(studentId) {
  // 清除之前的定时器
  if (timers.value[studentId]) {
    clearInterval(timers.value[studentId])
  }
  
  // 设置初始倒计时
  countdowns.value[studentId] = 30
  console.log(`开始倒计时: 学生ID ${studentId}, 倒计时: ${countdowns.value[studentId]}`)
  console.log('当前countdowns状态:', countdowns.value)
  
  // 强制更新UI
  nextTick(() => {
    console.log('UI更新后的countdowns状态:', countdowns.value)
  })
  
  // 创建新的定时器
  timers.value[studentId] = setInterval(() => {
    countdowns.value[studentId]--
    console.log(`倒计时更新: 学生ID ${studentId}, 剩余: ${countdowns.value[studentId]}`)
    if (countdowns.value[studentId] <= 0) {
      clearInterval(timers.value[studentId])
      delete timers.value[studentId]
      delete countdowns.value[studentId]
      console.log(`倒计时结束: 学生ID ${studentId}`)
    }
  }, 1000)
}

async function handlePickUp(studentId) {
  console.log(`handlePickUp被调用，studentId: ${studentId}, 类型: ${typeof studentId}`)
  console.log(`当前倒计时状态:`, countdowns.value)
  console.log(`该学生的倒计时:`, countdowns.value[studentId])
  
  if (!studentId || countdowns.value[studentId] > 0) {
    console.log(`按钮被禁用: 学生ID ${studentId}, 倒计时: ${countdowns.value[studentId]}`)
    return;
  }
  
  console.log(`点击预约接送: 学生ID ${studentId}`)
  
  try {
    startCountdown(studentId)
    const res = await pickUpChild({ studentId })
    if (res && res.data && res.data.respCode === '0000') {
      showCustomAlert('温馨提示', '预约接送成功！')
      // 预约成功后立即启动倒计时
      console.log(`预约成功，启动倒计时: 学生ID ${studentId}`)
      startCountdown(studentId)
    } else {
      showCustomAlert('温馨提示', res?.data?.respMsg || '预约接送失败')
    }
  } catch (e) {
    console.error('预约接送失败:', e)
    showCustomAlert('温馨提示', '预约接送失败')
  }
}

onMounted(() => {
  // 检查是否已登录
  const token = getToken()
  if (!token) {
    router.replace('/login')
    return
  }
  
  // 检查是否已绑定手机号
  const bindPhoneFlag = localStorage.getItem('bindPhoneV2')
  if (bindPhoneFlag !== '1') {
    router.replace('/bindPhone')
    return
  }
  
  fetchStudentList()
})

onUnmounted(() => {
  // 清理所有定时器
  Object.values(timers.value).forEach(timer => {
    clearInterval(timer)
  })
})
</script>

<style scoped>
.parent-page {
  max-width: 500px;
  margin: 50px auto;
  padding: 20px;
}

.student-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.student-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
}

.student-name {
  flex: 1;
  text-align: left;
}

.class-name {
  flex: 2;
  text-align: center;
  color: #666;
}

.reserve-btn {
  flex: 1;
  padding: 6px 16px;
  background: #42b983;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.reserve-btn:hover:not(.disabled) {
  background: #369870;
}

.reserve-btn.disabled {
  background: #ccc;
  cursor: not-allowed;
  color: #999;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

/* 自定义弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 280px;
  max-width: 90%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: modalFadeIn 0.3s ease;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.modal-header {
  padding: 20px 20px 0 20px;
  text-align: center;
}

.modal-header h3 {
  margin: 0;
  color: #333;
  font-size: 18px;
}

.modal-body {
  padding: 15px 20px;
  text-align: center;
}

.modal-body p {
  margin: 0;
  color: #666;
  font-size: 16px;
  line-height: 1.5;
}

.modal-footer {
  padding: 0 20px 20px 20px;
  text-align: center;
}

.modal-btn {
  background: #42b983;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 10px 30px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.modal-btn:hover {
  background: #369870;
}
</style> 